import { rem } from 'polished'
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { mediaQueryUp } from 'components/utils/breakpoint'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import useExperienceSearchLabels from 'components/Experiences/hooks/useExperienceSearchLabels'
import useTypingAnimation from 'hooks/useTypingAnimation'

import SearchLocationInput from 'components/Search/SearchForm/SearchLocationInput/SearchLocationInput'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import ExperiencesSearchDateSelectDropdown from './ExperiencesSearchDateSelectDropdown'

import { SearchMenuStates } from '../type'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { searchPaneInteractEventName } from 'components/Search/utils'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { EXPERIENCE_NLS_TYPING_OPTIONS } from 'constants/experience'
import useExperienceDateDisplayValue from 'components/Experiences/hooks/useExperienceDateDisplayValue'
import useExperienceRecentSearches from 'components/Experiences/hooks/useExperienceRecentSearches'

const ControlsContainer = styled.div`
  position: relative;
  display: grid;
  gap: ${rem(12)};
  grid-template:
    "location-input"
    "date-input"
    "actions" auto / 1fr;

  ${mediaQueryUp.tablet} {
    grid-template: "location-input date-input actions" auto / 1fr 1fr ${rem(112)};
  }
`

const LocationInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: location-input;
  position: relative;
`

const AnimatedPlaceholderOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  text-align: start;
  padding: ${rem(4)} ${rem(16)};
  background: ${(props) => props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${(props) => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${(props) => props.theme.borderRadius.XS};
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${rem(2)};
`

const DateInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: date-input;
`

const ActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-area: actions;
`

const SearchButton = styled(TextButton)`
  ${mediaQueryUp.tablet} {
    height: ${rem(50)};
  }
`

interface Props {
  onSearchButtonClick?: (e?: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>, searchItem?: App.SearchItem) => void
  onSearchPhraseChange?: (searchPhrase: string) => void
  disableSearch?: boolean,
  temporarySearchSubmitRef: React.MutableRefObject<App.TypeaheadSubmitRefData | null>
}

function ExperiencesSearchInputs(props: Props) {
  const {
    onSearchButtonClick,
    onSearchPhraseChange,
    disableSearch = false,
    temporarySearchSubmitRef,
  } = props

  const {
    naturalLanguageLocationLabel,
    naturalLanguageSearchExamples,
    searchLabel,
    whenLabel,
    anytimePlaceholder,
  } = useExperienceSearchLabels()
  const {
    activeMenu,
    checkinDate,
    checkoutDate,
    popularDestinations,
    disableRecentSearches,
    searchItem,
  } = useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const recentSearches = useExperienceRecentSearches()
  const [inputFocused, setInputFocused] = useState(false)

  useEffect(() => {
    const input = inputRef.current
    if (!input) return

    const handleFocus = () => setInputFocused(true)

    const handleBlur = () => {
      setTimeout(() => {
        const { activeElement } = document
        if (controlsContainerRef.current?.contains(activeElement)) return
        if (activeElement?.closest('[data-testid="place-select"]')) return
        setInputFocused(false)
      }, 0)
    }

    input.addEventListener('focus', handleFocus)
    input.addEventListener('blur', handleBlur)
    return () => {
      input.removeEventListener('focus', handleFocus)
      input.removeEventListener('blur', handleBlur)
    }
  }, [])

  const recentSearchItems = useMemo(() => {
    // Omits the rooms array from the recent search, we don't need it for experiences
    return recentSearches.map(recentSearch => ({
      searchItem: recentSearch.searchItem,
      dates: recentSearch.dates,
      categories: recentSearch.categories,
      vertical: recentSearch.vertical,
      recentSearchId: recentSearch.recentSearchId,
      dateAdded: recentSearch.dateAdded,
      rooms: [],
    }))
  }, [recentSearches])

  const animatedNaturalLanguagePlaceholder = useTypingAnimation(
    naturalLanguageSearchExamples,
    false,
    EXPERIENCE_NLS_TYPING_OPTIONS,
  )

  const showOverlay = !searchItem && !inputFocused

  const setActiveMenu = useCallback((menu: SearchMenuStates) => {
    globalSearchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu })
  }, [globalSearchDispatch])

  const closeMenu = useCallback(() => {
    setActiveMenu(SearchMenuStates.Closed)
  }, [setActiveMenu])

  const handleApply = useCallback(() => {
    onSearchButtonClick?.()
    closeMenu()
  }, [closeMenu, onSearchButtonClick])

  const handleLocationSelect = useCallback((searchItem?: App.SearchItem) => {
    if (!searchItem) return

    onSearchButtonClick?.(undefined, searchItem)
  }, [onSearchButtonClick])

  const onToggleMenu = useCallback((nextFilter?: SearchMenuStates) => {
    if (!nextFilter || activeMenu === nextFilter) {
      closeMenu()
    } else {
      setActiveMenu(nextFilter)
    }
  }, [activeMenu, closeMenu, setActiveMenu])

  const openLocationMenu = useCallback((e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation()
    document.dispatchEvent(new CustomEvent(searchPaneInteractEventName))
    onToggleMenu(SearchMenuStates.Locations)
  }, [onToggleMenu])

  const openDatePickerMenu = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    document.dispatchEvent(new CustomEvent(searchPaneInteractEventName))
    onToggleMenu(SearchMenuStates.Dates)
  }, [onToggleMenu])

  const inputRef = useRef<HTMLInputElement>(null)
  const controlsContainerRef = useRef<HTMLDivElement>(null)
  const dateTriggerRef = useRef<HTMLButtonElement>(null)

  const dateFieldDisplayValue = useExperienceDateDisplayValue(checkinDate, checkoutDate)

  return (
    <ControlsContainer ref={controlsContainerRef}>
      <LocationInputContainer>
        {showOverlay && (
          <AnimatedPlaceholderOverlay aria-hidden="true">
            <BodyText variant="medium" weight="bold" colour="normal-contrast">{naturalLanguageLocationLabel}</BodyText>
            <BodyText variant="medium" colour="normal-contrast" as="span">
              {animatedNaturalLanguagePlaceholder}
            </BodyText>
          </AnimatedPlaceholderOverlay>
        )}
        <SearchLocationInput
          data-testid="experiences-search-location-input"
          label={naturalLanguageLocationLabel}
          placeholder={naturalLanguageLocationLabel}
          variant="dropdown"
          onLocationToggle={openLocationMenu}
          searchVertical="experience"
          inputRef={inputRef}
          controlsContainerRef={controlsContainerRef}
          placeholderItems={popularDestinations}
          recentSearchItems={disableRecentSearches ? undefined : recentSearchItems}
          onChange={handleLocationSelect}
          onSearchPhraseChange={onSearchPhraseChange}
          temporarySearchSubmitRef={temporarySearchSubmitRef}
          allowExperienceNaturalLanguageSearch
          searchPath="/search/experiences"
          searchMapPath="/search/experiences/map"
        />
      </LocationInputContainer>
      <DateInputContainer>
        <SearchFormField
          ref={dateTriggerRef}
          label={whenLabel}
          placeholder={anytimePlaceholder}
          value={dateFieldDisplayValue}
          onClick={openDatePickerMenu}
        />
        <ExperiencesSearchDateSelectDropdown
          anchorRef={controlsContainerRef}
          triggerRef={dateTriggerRef}
          open={activeMenu === SearchMenuStates.Dates}
          onApply={handleApply}
          onDecline={closeMenu}
        />
      </DateInputContainer>
      <ActionsContainer>
        <SearchButton kind="primary" type="submit" size="large" onClick={onSearchButtonClick} disabled={disableSearch}>{searchLabel}</SearchButton>
      </ActionsContainer>
    </ControlsContainer>
  )
}

export default memo(ExperiencesSearchInputs)
